/* eslint-disable max-classes-per-file */
import { type CfAccountInfo } from '@chainflip/rpc/types';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { type ApiPromise } from '@polkadot/api';
import { type Signer, type SubmittableExtrinsic } from '@polkadot/api/types';
import { type QueryClient } from '@tanstack/react-query';
import ExtrinsicSDK from '@/shared/utils/ExtrinsicSDK';

type SendCallback = Parameters<SubmittableExtrinsic<'promise'>['send']>[0];

class PolkadotExtrinsicSDK extends ExtrinsicSDK {
  constructor(
    account: CfAccountInfo,
    apiPromise: ApiPromise,
    accountId: `cF${string}`,
    refetch: () => void,
    queryClient: QueryClient,
    protected readonly signer: Signer,
  ) {
    super(account, apiPromise, accountId, refetch, queryClient);
  }

  override async submit(extrinsic: SubmittableExtrinsic<'promise'>, handleResult: SendCallback) {
    return extrinsic.signAndSend(this.accountIdHex, { signer: this.signer }, handleResult);
  }

  override async signAndSendBatch(callback: (sdk: ExtrinsicSDK) => void) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const batchSdk = new BatchPolkadotExtrinsicSDK(
      this.account,
      this.apiPromise,
      this.accountId,
      this.refetch,
      this.queryClient,
      this.signer,
    );

    callback(batchSdk);
    return this.signAndSend(this.apiPromise.tx.environment.batch(batchSdk.getExtrinsics()));
  }

  // eslint-disable-next-line class-methods-use-this
  override async signAccountCreationPayload(
    _asset: ChainflipAsset,
    _refundAddress: string,
  ): Promise<never> {
    throw new Error('Method not supported for native State Chain accounts.');
  }
}

class BatchPolkadotExtrinsicSDK extends PolkadotExtrinsicSDK {
  private readonly extrinsics: SubmittableExtrinsic<'promise'>[] = [];

  override async signAndSend(extrinsic: SubmittableExtrinsic<'promise'>) {
    this.extrinsics.push(extrinsic);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Promise.resolve(null as any);
  }

  getExtrinsics() {
    return this.extrinsics;
  }
}

export default PolkadotExtrinsicSDK;
